import { yup } from '@framework/validation';
import { fromPairs } from 'lodash';
import { debug } from '../../../../utils/debug';
import { ExpressionType } from '../../IExpression';
import { createExpressionType } from '../../IExpressionType';
import { IComputeExpressionValueReference } from '../../compute/ComputeExpression';
import { ComputeMath } from '../../compute/ComputeMath';
import { ComputeExpressionSchema } from '../../compute/validation/ComputeExpressionSchema';
import { Datatype } from '../../datatype/Datatype';
import { IReference } from '../../references/IReference';
import { IFunctionExpression } from './IFunctionExpression';

const getFunctionReferences = (expression: IFunctionExpression) =>
  expression.value.value
    .filter(
      (i): i is IComputeExpressionValueReference =>
        i.type === 'compute-expression-reference',
    )
    .map(i => {
      if (i.attrs.reference == null) {
        console.warn(`[getFunctionReferences] reference is null`, i);
      }
      return i.attrs.reference;
    })
    .filter(r => r != null);

interface IComputeToken {
  name: string;
  reference: IReference;
}

const compileExpressionWithIds = (expression: IFunctionExpression) => {
  const tokens: IComputeToken[] = [];
  let currentToken = 0;
  const compiled = expression.value.value
    .map(item => {
      switch (item.type) {
        case 'compute-expression-reference': {
          const name = `var${currentToken++}`;
          tokens.push({ name, reference: item.attrs.reference });
          return name;
        }

        case 'text':
          return item.text;
      }
    })
    .join('');

  return { compiled, tokens };
};

export const FunctionExpressionType = createExpressionType<IFunctionExpression>(
  {
    type: ExpressionType.Function,
    schema: yup.object({
      type: yup.string().exact(ExpressionType.Function).required(),
      value: ComputeExpressionSchema.required(),
    }),
    evaluate(expression, scope) {
      const { compiled, tokens } = compileExpressionWithIds(expression);
      // logger.log(
      //   'FunctionExpressionType.evaluate',
      //   expression,
      //   'compiled',
      //   compiled,
      //   scope.getAllReferences()
      // );

      try {
        // Non possiamo utilizzare `scope.getAllReferences()` perché gli UUID
        // non sono nomi di variabili validi (contengono trattini).
        // Creiamo quindi un array di coppie [nome, valore] da utilizzare
        // come variabili.
        const tokensWithValues = tokens.map(t => [
          t.name,
          scope.getReference(
            t.reference,
            t.reference.field.type === Datatype.Number &&
              scope.math.considerNullsAsZero
              ? 0
              : null,
          ),
        ]);

        // Se una variabile non è definita, non possiamo valutare l'espressione.
        if (
          !scope.math.allowsMathWithNulls &&
          tokensWithValues.some(t => t[1] == null)
        ) {
          return null;
        }

        const variables = fromPairs(tokensWithValues);

        const result = ComputeMath.evaluate(compiled, variables);
        debug.log('expression.compute.evaluate', 'Calcolo eseguito', { result, compiled, variables }); // prettier-ignore

        // Per evitare di avere risultati come 0.9999999999999999, arrotondiamo
        // il risultato alla 12esima cifra decimale (la precisione di JS è di 14
        // cifre)
        return ComputeMath.round(result, 12);
      } catch (e) {
        console.error('FunctionExpressionType.evaluate', e, { expression, scope }); // prettier-ignore
        return null;
      }
    },
    getReferences(expression, collect) {
      return getFunctionReferences(expression);
    },
    toString(expression, toString) {
      return expression.value.raw;
    },
  },
);
